<template>
  <div class="lg">
    <!--
      좌측 잉크 패널.
      로그인은 앱에서 유일하게 셸(좌측 레일 · 상단바) 밖에 있는 화면이라, 예전에는
      보라색 그라디언트 위에 흰 카드 하나만 떠 있어 이 앱의 나머지 화면과 아무 관계가
      없어 보였다. 잉크(--color-ink)는 좌측 레일과 같은 색이다 — 로그인한 순간
      왼쪽에 남는 그 판이 여기서 먼저 보인다.
    -->
    <aside class="lg__brand">
      <div class="lg__brand-body">
        <div class="lg__mark">
          <span class="lg__mark-glyph" aria-hidden="true">
            <svg viewBox="0 0 28 28" width="28" height="28" fill="none">
              <rect x="0.5" y="0.5" width="27" height="27" rx="8.5" fill="currentColor" opacity="0.18" />
              <path
                d="M8.5 19.5V11M14 19.5V8.5M19.5 19.5v-5.5"
                stroke="currentColor"
                stroke-width="2.4"
                stroke-linecap="round"
              />
            </svg>
          </span>
          <span class="lg__wordmark">IRUDA <span>WORK</span></span>
        </div>

        <h1 class="lg__headline">출근해서 제일 먼저<br />여는 화면.</h1>

        <p class="lg__lede">
          일정 · 업무 · 보고가 한 곳에 있습니다. 어제 못 끝낸 일, 내 차례인 결재,
          이번 주 대직까지 — 들어오면 처리할 것부터 보입니다.
        </p>

        <ul class="lg__points">
          <li v-for="p in points" :key="p.title" class="lg__point">
            <el-icon class="lg__point-icon"><component :is="p.icon" /></el-icon>
            <span class="lg__point-text">
              <b>{{ p.title }}</b>
              <span>{{ p.desc }}</span>
            </span>
          </li>
        </ul>
      </div>

      <p class="lg__copy only-tablet-up">
        © <span class="u-num">2026</span> 이루다 · 사내 업무 관리 시스템
      </p>
    </aside>

    <main class="lg__pane">
      <div class="lg__card">
        <h2 class="lg__card-title">로그인</h2>
        <p class="lg__card-sub">사내 계정으로 이루다 WORK 에 들어갑니다.</p>

        <!-- 카카오 콜백이 /login?error=… 로 돌려보내는 실패 사유가 여기 뜬다.
             카드 맨 위가 아니라 버튼 **바로 위**에 둔다 — 다시 누를 버튼과
             실패 이유가 떨어져 있으면 위쪽 문구를 읽지 않고 또 누른다. -->
        <p v-if="errorMessage" class="lg__error" role="alert">{{ errorMessage }}</p>

        <el-button class="lg__kakao" :loading="loading" @click="loginWithKakao">
          <svg class="lg__kakao-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path
              fill="currentColor"
              d="M12 3c5.799 0 10.5 3.664 10.5 8.185 0 4.52-4.701 8.184-10.5 8.184a13.5 13.5 0 0 1-1.727-.11l-4.408 2.883c-.501.265-.678.236-.472-.413l.892-3.678c-2.88-1.46-4.785-3.99-4.785-6.866C1.5 6.665 6.201 3 12 3z"
            />
          </svg>
          카카오로 로그인
        </el-button>
        <p class="lg__hint">처음 로그인하면 계정이 자동으로 만들어집니다.</p>

        <!--
          사번/비밀번호 로그인 폼은 **의도적으로 없다.**

          리디자인 목업에는 카카오 아래에 사번 로그인이 그려져 있었는데, 뒤가 비어 있다:
          server/routes/auth.js 에는 kakao / kakao/callback / status / logout 넷뿐이고
          User 모델에 사번·비밀번호 컬럼이 없다. 화면만 만들어 두면 비활성 입력 두 칸과
          눌리지 않는 버튼이 로그인 화면의 절반을 차지한다 — 첫 화면에서 "이 서비스는
          반쯤 만들다 만 것" 이라는 인상을 준다. 없는 편이 낫다.

          엔드포인트가 생기면 여기에 사번/비밀번호 필드 + 제출 핸들러를 추가한다.
          디자인은 캔버스의 Login 아트보드에 남아 있다.
        -->
      </div>

      <p class="lg__copy lg__copy--pane only-mobile">
        © <span class="u-num">2026</span> 이루다 · 사내 업무 관리 시스템
      </p>
    </main>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { Calendar, Grid, Document } from '@element-plus/icons-vue'

const LOGIN_ERRORS = {
  authorization_failed: '카카오 로그인 인증에 실패했습니다.',
  nickname_required: '닉네임 정보가 필요합니다. 카카오 계정에서 프로필을 공개로 설정해주세요.',
  account_disabled: '비활성화된 계정입니다. 관리자에게 문의하세요.',
  session_error: '로그인 세션을 만들지 못했습니다. 다시 시도해주세요.',
  login_failed: '로그인에 실패했습니다. 다시 시도해주세요.'
}

export default {
  name: 'Login',
  components: {
    Calendar,
    Grid,
    Document
  },
  setup() {
    const router = useRouter()
    const store = useStore()
    const loading = ref(false)
    const errorMessage = ref('')

    // 잉크 패널의 세 줄. 실제로 하단 탭바에 있는 세 화면과 같은 순서다.
    const points = [
      { icon: 'Calendar', title: '캘린더', desc: '일정 · 대직 · 외근을 한 달치로' },
      { icon: 'Grid', title: '업무 보드', desc: '상태별로 옮기며 진행 상황을 본다' },
      { icon: 'Document', title: '보고 · 결재', desc: '일일/주간 보고를 올리고 결재까지' }
    ]

    const loginWithKakao = () => {
      loading.value = true
      window.location.href = '/api/auth/kakao'
    }

    onMounted(async () => {
      // 카카오 콜백이 /login?error=… 로 돌려보낸 실패 사유 (server/routes/auth.js)
      const error = new URLSearchParams(window.location.search).get('error')
      if (error) errorMessage.value = LOGIN_ERRORS[error] || '로그인 중 오류가 발생했습니다.'

      // 서버 세션은 살아 있는데 이 기기에 사용자 정보만 없는 경우(브라우저 저장소를 지웠을 때) 바로 들어간다.
      // replace 인 이유: 뒤로가기로 이 화면에 다시 서지 않게.
      try {
        await store.dispatch('checkAuth')
        router.replace('/today')
      } catch (e) {
        // 로그인 전 — 이 화면에 머문다
      }
    })

    return {
      loading,
      errorMessage,
      loginWithKakao,
      points
    }
  }
}
</script>

<style scoped>
/*
 * 지역 변수 두 묶음.
 * 하나는 잉크 위 최상위 글자(#fff) — SideNav 도 잉크 위에서는 같은 리터럴을 쓴다.
 * 다른 하나는 카카오 브랜드 색이다. 이건 우리 팔레트가 아니라 카카오가 정한 값이라
 * 토큰으로 승격시키지 않고 이 컴포넌트 안에만 둔다. 특히 글자색은 #191600 이어야
 * 한다 — 노랑 위 흰 글자는 대비 1.2:1 로 사실상 안 보인다.
 */
.lg {
  --lg-on-ink: #fff;
  --lg-glow: rgba(37, 99, 235, 0.34); /* --color-primary 를 투명하게. 방사형 글로우 전용 */
  --lg-kakao: #fee500;
  --lg-kakao-hover: #f2d900;
  --lg-kakao-fg: #191600;

  display: flex;
  min-height: 100vh;
  min-height: 100dvh; /* 모바일 주소창 접힘 대응 */
  background: var(--color-bg);
}

/* ---------- 좌측 잉크 패널 ---------- */

.lg__brand {
  position: relative;
  overflow: hidden;
  flex: 0 0 56%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: var(--sp-8);
  padding: var(--sp-12) var(--sp-10);
  padding-left: calc(var(--sp-10) + var(--safe-left));
  background: var(--color-ink);
  color: var(--color-ink-text);
}

/* 격자. 56px 은 잉크 패널의 아이콘(28px)의 두 배 — 눈에 걸리지 않을 만큼 크고
   판이 비어 보이지 않을 만큼 촘촘하다. */
.lg__brand::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(to right, var(--color-ink-line) 1px, transparent 1px),
    linear-gradient(to bottom, var(--color-ink-line) 1px, transparent 1px);
  background-size: 56px 56px;
  opacity: 0.4;
  pointer-events: none;
}

/* 우상단 글로우. 헤드라인 쪽(좌하단)은 건드리지 않아 글자 대비가 떨어지지 않는다. */
.lg__brand::after {
  content: '';
  position: absolute;
  top: -160px;
  right: -160px;
  width: 560px;
  height: 560px;
  background: radial-gradient(circle, var(--lg-glow) 0%, transparent 68%);
  pointer-events: none;
}

.lg__brand-body,
.lg__copy {
  position: relative;
  z-index: 1;
}

.lg__mark {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  margin-bottom: var(--sp-10);
}

.lg__mark-glyph {
  display: inline-flex;
  color: var(--color-primary);
}

.lg__wordmark {
  font-size: var(--fs-lg);
  font-weight: var(--fw-semibold);
  letter-spacing: 0.02em;
  color: var(--lg-on-ink);
}

.lg__wordmark span {
  color: var(--color-ink-text-dim);
  font-weight: var(--fw-regular);
}

/* 40px 은 ≥1200px 기준. 태블릿에서 56% 폭은 430px 밖에 안 되므로 같이 줄인다
   (고정 40px 이면 "출근해서 제일" 에서 한 번 더 접힌다). */
.lg__headline {
  margin: 0;
  font-size: clamp(28px, 3.4vw, 40px);
  line-height: 1.32;
  letter-spacing: -0.035em;
  font-weight: var(--fw-semibold);
  color: var(--lg-on-ink);
}

.lg__lede {
  margin: var(--sp-5) 0 0;
  max-width: 34em;
  font-size: var(--fs-lg);
  line-height: 1.75;
  color: var(--color-ink-text-dim);
}

.lg__points {
  list-style: none;
  margin: var(--sp-10) 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--sp-5);
}

.lg__point {
  display: flex;
  align-items: flex-start;
  gap: var(--sp-3);
}

.lg__point-icon {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: var(--r-md);
  background: var(--color-ink-surface);
  border: 1px solid var(--color-ink-line);
  color: var(--color-ink-text-bright);
  font-size: var(--fs-lead);
}

.lg__point-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: 3px;
}

.lg__point-text b {
  font-size: var(--fs-md);
  font-weight: var(--fw-semibold);
  color: var(--lg-on-ink);
}

.lg__point-text span {
  font-size: var(--fs-sm);
  color: var(--color-ink-text-faint);
}

.lg__copy {
  margin: 0;
  font-size: var(--fs-xs);
  color: var(--color-ink-text-faint);
}

/* ---------- 우측 폼 ---------- */

.lg__pane {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--sp-5);
  padding: var(--sp-8) var(--sp-6);
  padding-right: calc(var(--sp-6) + var(--safe-right));
  padding-bottom: calc(var(--sp-8) + var(--safe-bottom));
}

.lg__card {
  width: 100%;
  max-width: 368px;
}

.lg__card-title {
  margin: 0;
  font-size: var(--fs-xl);
  font-weight: var(--fw-semibold);
  letter-spacing: -0.02em;
  color: var(--color-text);
}

.lg__card-sub {
  margin: var(--sp-1) 0 var(--sp-6);
  font-size: var(--fs-md);
  color: var(--color-text-muted);
}

/* 좌측 3px 액센트는 SectionCard 의 tone="danger" 와 같은 어법이다.
   테두리를 danger-soft 로 두르면 배경과 같은 색이라 있으나 마나였다. */
.lg__error {
  margin: 0 0 var(--sp-3);
  padding: var(--sp-3);
  border-radius: var(--r-md);
  border-left: 3px solid var(--color-danger);
  background: var(--color-danger-soft);
  color: var(--color-danger-strong);
  font-size: var(--fs-sm);
  line-height: 1.5;
}

.lg__kakao {
  --el-button-bg-color: var(--lg-kakao);
  --el-button-border-color: var(--lg-kakao);
  --el-button-text-color: var(--lg-kakao-fg);
  --el-button-hover-bg-color: var(--lg-kakao-hover);
  --el-button-hover-border-color: var(--lg-kakao-hover);
  --el-button-hover-text-color: var(--lg-kakao-fg);
  --el-button-active-bg-color: var(--lg-kakao-hover);
  --el-button-active-border-color: var(--lg-kakao-hover);
  --el-button-active-text-color: var(--lg-kakao-fg);

  width: 100%;
  height: 46px;
  border-radius: var(--r-md);
  font-size: var(--fs-md);
  font-weight: var(--fw-semibold);
}

.lg__kakao-icon {
  margin-right: 7px;
}

.lg__hint {
  margin: var(--sp-2) 0 0;
  text-align: center;
  font-size: var(--fs-xs);
  color: var(--color-text-subtle);
  line-height: 1.5;
}

.lg__copy--pane {
  color: var(--color-text-subtle);
}

/* ---------- 모바일: 잉크 패널을 상단 배너로 ---------- */

@media (max-width: 767px) { /* --bp-mobile-max */
  .lg {
    flex-direction: column;
  }

  .lg__brand {
    flex: 0 0 auto;
    gap: 0;
    /* 단축 padding 은 기본 규칙의 safe-left/right 보정까지 덮는다.
       가로 모드 노치 단말에서 배너 글자가 노치 밑으로 들어간다. */
    padding: calc(var(--sp-7) + var(--safe-top)) var(--sp-5) var(--sp-7);
    padding-left: calc(var(--sp-5) + var(--safe-left));
    padding-right: calc(var(--sp-5) + var(--safe-right));
  }

  .lg__mark {
    margin-bottom: var(--sp-4);
  }

  /* 배너에서는 헤드라인 한 줄만 남긴다. 리드 문단과 세 줄 목록은
     폼을 첫 화면 밖으로 밀어내기만 한다 — 로그인하러 온 사람에게 소개문은 필요 없다. */
  .lg__headline {
    font-size: 24px;
    line-height: 1.34;
  }

  .lg__lede,
  .lg__points {
    display: none;
  }

  .lg__pane {
    flex: 1;
    justify-content: flex-start;
    padding: var(--sp-6) var(--sp-5);
    padding-left: calc(var(--sp-5) + var(--safe-left));
    padding-right: calc(var(--sp-5) + var(--safe-right));
    padding-bottom: calc(var(--sp-6) + var(--safe-bottom));
  }

  .lg__card {
    max-width: none;
  }

  /* 모바일 주 버튼은 엄지로 누른다 */
  .lg__kakao {
    height: var(--tap-lg);
  }
}
</style>
